/**
 * TCR-2542 — Registry của Trung tâm báo cáo: danh mục báo cáo khai báo dạng dữ liệu.
 *
 * Mục tiêu: thêm một báo cáo mới = THÊM ĐÚNG 1 PHẦN TỬ vào `REPORT_REGISTRY`
 * (id, tên, nhóm, mô tả, permission key, lazy loader). Không phải sửa menu, không
 * phải sửa route — cả hai tự dẫn xuất từ registry:
 *   - Menu "Trung tâm báo cáo" gate bằng `REPORT_CENTER_ANY_PERMISSIONS` (hợp OR
 *     mọi key của registry) — xem menuItems.tsx.
 *   - Route `/report-center/:reportId` render `ReportOutlet` → tra id ở đây.
 *
 * Permission key: DÙNG KEY CÓ SẴN trong catalogue
 * (`tcr-erp-shared/src/common/constants/permissions.ts`) — TCR-2542 chỉ đụng FE,
 * key mới (reports.<nhóm>.<tên>.view) sẽ được cấp ở ticket báo cáo tương ứng khi
 * có thay đổi shared + BE (quy trình 4 bước xem .brief-common.md).
 */
import type { ComponentType, LazyExoticComponent } from 'react';
import { lazy } from 'react';

/** Nhóm báo cáo trên trang danh mục — thứ tự mảng = thứ tự hiển thị. */
export type ReportGroupId = 'sales' | 'inventory' | 'reconciliation' | 'operations' | 'finance' | 'hr';

export interface ReportGroupDef {
  id: ReportGroupId;
  label: string;
  /** 1 dòng mô tả nhóm — giúp người mới biết nhóm này trả lời câu hỏi gì. */
  description: string;
}

export const REPORT_GROUPS: ReportGroupDef[] = [
  {
    id: 'sales',
    label: 'Bán hàng',
    description: 'Doanh số, đơn hàng, hiệu suất bán theo ngày / kênh / sản phẩm.',
  },
  {
    id: 'inventory',
    label: 'Kho',
    description: 'Tồn kho, biến động hàng hoá, cảnh báo thiếu và ứ đọng.',
  },
  {
    id: 'reconciliation',
    label: 'Đối soát',
    description: 'Đối chiếu tiền sàn, COD, hoàn huỷ với sổ sách nội bộ.',
  },
  {
    id: 'operations',
    label: 'Điều hành',
    description: 'Bức tranh tổng cho chủ shop: tăng trưởng, lợi nhuận, cảnh báo.',
  },
  {
    // TCR-2584 — tách khỏi "Bán hàng": lãi gộp là số kế toán/quản trị, quyền xem riêng (inventory.costing.view).
    id: 'finance',
    label: 'Tài chính',
    description: 'Lãi gộp, biên lợi nhuận và độ phủ giá vốn.',
  },
  {
    // TCR-2586 / TCR-2587 — báo cáo nhân sự dùng key hrm.* của phân hệ Nhân sự.
    id: 'hr',
    label: 'Nhân sự',
    description: 'Quy mô và biến động nhân sự, chuyên cần và điểm KPI công việc theo phòng ban.',
  },
];

export interface ReportRegistryEntry {
  /** Định danh ổn định — dùng trong URL /report-center/<id>. KHÔNG đổi sau khi xuất bản. */
  id: string;
  name: string;
  groupId: ReportGroupId;
  /** Mô tả 1 câu trên thẻ danh mục: báo cáo này trả lời câu hỏi gì. */
  description: string;
  /** Permission key gác cả thẻ danh mục lẫn route — dùng key có sẵn trong catalogue. */
  permissionKey: string;
  /** true → thẻ hiện nhãn "Mới" đến khi có báo cáo khác ra mắt sau nó. */
  isNew?: boolean;
  /** Lazy loader của trang báo cáo — outlet import đúng lúc user mở báo cáo. */
  component: LazyExoticComponent<ComponentType>;
}

/**
 * DANH MỤC BÁO CÁO — thêm báo cáo mới tại đây (1 phần tử).
 */
export const REPORT_REGISTRY: ReportRegistryEntry[] = [
  {
    // TCR-2551 (D01) — ĐẦU danh sách; số lấy lại từ service R01/R03/R08/R17/R12/R14/R07/R04 nên khớp báo cáo chi tiết.
    id: 'executive-dashboard',
    name: 'Dashboard điều hành',
    groupId: 'operations',
    description: 'Một màn nhìn nhanh doanh số, hủy hoàn, tồn kho, đối soát và cảnh báo vận hành; bấm từng khối để sang báo cáo chi tiết.',
    permissionKey: 'reports.dashboard.executive.view',
    isNew: true,
    component: lazy(() => import('./ExecutiveDashboard')),
  },
  {
    // TCR-2543 (R01) — báo cáo THẬT đầu tiên của Wave 1, đọc read-model report_daily_sales.
    id: 'sales-overview',
    name: 'Doanh số tổng quan đa kênh',
    groupId: 'sales',
    description: 'GMV, số đơn, giá trị đơn trung bình và tỷ trọng từng kênh sàn — so với kỳ trước.',
    permissionKey: 'reports.sales.overview.view',
    isNew: true,
    component: lazy(() => import('./SalesOverviewReport')),
  },
  {
    // TCR-2550 (R17) — heatmap thứ × giờ, đọc hour_buckets của read-model report_daily_sales.
    id: 'sales-rhythm',
    name: 'Nhịp bán hàng theo giờ và thứ',
    groupId: 'sales',
    description: 'Giờ và thứ nào khách đặt nhiều nhất — để chọn khung livestream, khuyến mãi và xếp người.',
    permissionKey: 'reports.sales.rhythm.view',
    isNew: true,
    component: lazy(() => import('./SalesRhythmReport')),
  },
  {
    // TCR-2544 (R02) — xếp hạng shop, tỷ trọng kênh, COD vs ví điện tử từ read-model report_daily_sales.
    id: 'sales-by-breakdown',
    name: 'Doanh số theo shop, kênh, thanh toán',
    groupId: 'sales',
    description: 'Shop nào bán tốt nhất, kênh nào chiếm tỷ trọng lớn, khách trả bằng COD hay ví điện tử.',
    permissionKey: 'reports.sales.by_shop.view',
    isNew: true,
    component: lazy(() => import('./SalesBreakdownReport')),
  },
  {
    // TCR-2545 (R03) — phễu trạng thái, lý do hủy / hoàn, hoàn theo shop và SP.
    id: 'sales-cancellations',
    name: 'Hủy, hoàn, đổi trả',
    groupId: 'sales',
    description: 'Đơn hủy ở khâu nào, khách hoàn / đổi trả vì sao, shop và sản phẩm nào bị nhiều nhất.',
    permissionKey: 'reports.sales.cancellations.view',
    isNew: true,
    component: lazy(() => import('./CancellationsReport')),
  },
  {
    // TCR-2547 (R08) — tích hợp Wave 1/2.
    id: 'sales-products',
    name: 'Sản phẩm, danh mục, thương hiệu bán chạy',
    groupId: 'sales',
    description: 'Sản phẩm, danh mục và thương hiệu nào đang kéo doanh số, kèm nhóm ABC và cảnh báo hàng chưa gắn sản phẩm.',
    permissionKey: 'reports.sales.products.view',
    isNew: true,
    component: lazy(() => import('./SalesProductsReport')),
  },
  {
    // TCR-2553 (R09) — tích hợp Wave 1/2.
    id: 'sales-sku-performance',
    name: 'Hiệu suất SKU',
    groupId: 'sales',
    description: 'SKU nào bán nhanh, SKU nào đang chậm lại: tốc độ bán 7/30 ngày, xu hướng so kỳ trước và nhóm ABC.',
    permissionKey: 'reports.sales.products.view',
    isNew: true,
    component: lazy(() => import('./SkuPerformanceReport')),
  },
  {
    // TCR-2552 (R04) — tích hợp Wave 1/2.
    id: 'sales-vs-accounted',
    name: 'Doanh số sàn so với doanh thu đã hạch toán',
    groupId: 'reconciliation',
    description: 'Chênh lệch giữa tiền bán trên sàn và doanh thu đã ghi sổ, đơn nào chưa ghi sổ.',
    permissionKey: 'reports.ecom.reconciliation.view',
    isNew: true,
    component: lazy(() => import('./SalesVsAccountedReport')),
  },
  {
    // TCR-2546 (R07) — tích hợp Wave 1/2.
    id: 'ecom-reconciliation',
    name: 'Đối soát sàn',
    groupId: 'reconciliation',
    description: 'Đơn nào chưa đối soát, tồn bao lâu và sàn đang giữ bao nhiêu phí.',
    permissionKey: 'reports.ecom.reconciliation.view',
    isNew: true,
    component: lazy(() => import('./EcomReconciliationReport')),
  },
  {
    // TCR-2548 (R12) — tích hợp Wave 1/2.
    id: 'inventory-stock',
    name: 'Tồn kho hiện tại và giá trị',
    groupId: 'inventory',
    description: 'Mỗi kho còn bao nhiêu hàng, giá trị bao nhiêu, bao nhiêu đang giữ chỗ cho đơn.',
    permissionKey: 'reports.inventory.stock.view',
    isNew: true,
    component: lazy(() => import('./InventoryStockReport')),
  },
  {
    // TCR-2549 (R13) — tích hợp Wave 1/2.
    id: 'inventory-movement',
    name: 'Nhập - xuất - tồn theo kỳ',
    groupId: 'inventory',
    description: 'Đầu kỳ có bao nhiêu, nhập xuất chuyển điều chỉnh bao nhiêu, cuối kỳ còn bao nhiêu.',
    permissionKey: 'reports.inventory.movement.view',
    isNew: true,
    component: lazy(() => import('./InventoryMovementReport')),
  },
  {
    // TCR-2554 (R14) — tích hợp Wave 1/2.
    id: 'inventory-cover',
    name: 'Số ngày tồn và hàng tồn chậm',
    groupId: 'inventory',
    description: 'Hàng còn bán được bao nhiêu ngày, SKU nào sắp hết, hết hàng hoặc tồn chậm.',
    permissionKey: 'reports.inventory.stock.view',
    isNew: true,
    component: lazy(() => import('./InventoryCoverReport')),
  },
  {
    // TCR-2093 — Báo cáo Kho vận, tích hợp ở TCR-2537 E2. Dùng LẠI key `reports.inventory.movement.view`
    // (không thêm key riêng) — khớp BE ReportWarehouseOpsController. Route /report-center/warehouse-ops tự dẫn
    // xuất từ registry nên không cần sửa App.tsx.
    id: 'warehouse-ops',
    name: 'Kho vận',
    groupId: 'inventory',
    description: 'Sản lượng xuất nhập, năng suất nhân viên, hàng về, hoàn/hỏng, kiểm kho và cảnh báo bất thường theo kỳ.',
    permissionKey: 'reports.inventory.movement.view',
    isNew: true,
    component: lazy(() => import('./WarehouseOpsReport')),
  },
  {
    // TCR-2556 (R18) — cùng nguồn R01, dùng key nhịp bán hàng.
    id: 'sales-events',
    name: 'Sự kiện sale và mùa vụ',
    groupId: 'sales',
    description: 'Doanh số từng ngày kèm đánh dấu sự kiện, so ngày sale với ngày thường.',
    permissionKey: 'reports.sales.rhythm.view',
    isNew: true,
    component: lazy(() => import('./SalesEventsReport')),
  },
  {
    // TCR-2555 (R16) — id CỐ ĐỊNH: R20 link cứng /report-center/ecom-stock-sync.
    id: 'ecom-stock-sync',
    name: 'Đồng bộ tồn lên sàn',
    groupId: 'inventory',
    description: 'Đẩy tồn lên sàn lỗi ở đâu: kênh, shop, SKU, lý do, tuổi tồn đọng và trạng thái đẩy hiện tại.',
    permissionKey: 'reports.inventory.stock.view',
    isNew: true,
    component: lazy(() => import('./StockSyncReport')),
  },
  {
    // TCR-2558 (R21) — dùng key kho vận (khớp BE).
    id: 'logistics-delivery',
    name: 'Giao hàng và vận chuyển',
    groupId: 'operations',
    description: 'Tỷ lệ giao thành công/hoàn, thời gian giao trung bình theo hãng và kênh, đơn giao chậm.',
    permissionKey: 'reports.inventory.movement.view',
    isNew: true,
    component: lazy(() => import('./DeliveryReport')),
  },
  {
    // TCR-2557 (R20).
    id: 'ops-exceptions',
    name: 'Ngoại lệ vận hành',
    groupId: 'operations',
    description: 'Đơn nào đang kẹt, kẹt vì lý do gì, bao lâu, kèm nút sang màn xử lý.',
    permissionKey: 'reports.dashboard.executive.view',
    isNew: true,
    component: lazy(() => import('./OpsExceptionsReport')),
  },
  {
    // TCR-2560 (R23).
    id: 'data-health',
    name: 'Sức khỏe dữ liệu báo cáo',
    groupId: 'operations',
    description: 'Số trên các báo cáo có đáng tin không: độ tươi, ngày thiếu, hàng chưa map, giá vốn 0 và đối chiếu rollup với đơn gốc.',
    permissionKey: 'reports.dashboard.executive.view',
    isNew: true,
    component: lazy(() => import('./DataHealthReport')),
  },
  {
    // TCR-2559 (R22) — hóa đơn điện tử; key tạm dùng reports.ecom.reconciliation.view (khớp BE REPORT_EINVOICE_PERMISSION).
    id: 'accounting-einvoice',
    name: 'Hóa đơn điện tử',
    groupId: 'reconciliation',
    description: 'Hóa đơn đã xuất, hóa đơn lỗi tồn bao lâu và đơn đủ điều kiện nhưng chưa xuất hóa đơn.',
    permissionKey: 'reports.ecom.reconciliation.view',
    isNew: true,
    component: lazy(() => import('./EInvoiceReport')),
  },
  {
    // TCR-2584 — lãi gộp ước tính; key kế toán `inventory.costing.view` (khớp BE ReportGrossMarginController).
    id: 'gross-margin',
    name: 'Lãi gộp ước tính',
    groupId: 'finance',
    description: 'Hàng nào đang có lời: doanh thu đã hóa đơn trừ giá vốn ước tính, biên % và độ phủ giá vốn.',
    permissionKey: 'inventory.costing.view',
    isNew: true,
    component: lazy(() => import('./GrossMarginReport')),
  },
  {
    // TCR-2585 — hoàn hàng & huỷ đơn sàn; cùng key với R03 (khớp BE).
    id: 'returns-ops',
    name: 'Hoàn hàng & huỷ đơn sàn',
    groupId: 'operations',
    description: 'Hàng khách trả lại đang tồn đọng bao lâu, shop nào bị nhiều nhất, yêu cầu huỷ nào chưa vào ERP.',
    permissionKey: 'reports.sales.cancellations.view',
    isNew: true,
    component: lazy(() => import('./ReturnsOpsReport')),
  },
  {
    // TCR-2586 — BE cho phép hrm.reports.view HOẶC hrm.employees.view (ANY-of); thẻ menu gác theo key chính.
    id: 'hr-headcount',
    name: 'Quy mô & biến động nhân sự theo phòng ban',
    groupId: 'hr',
    description: 'Đang có bao nhiêu người, phòng ban nào đang lớn hay thu nhỏ, kỳ này tuyển mới và nghỉ việc bao nhiêu.',
    permissionKey: 'hrm.reports.view',
    isNew: true,
    component: lazy(() => import('./HrHeadcountReport')),
  },
  {
    // TCR-2587 — BE cho phép hrm.attendance.view / hrm.kpi.view / hrm.reports.view (ANY-of); thẻ menu gác theo key chính.
    id: 'hr-attendance-kpi',
    name: 'Chuyên cần & KPI theo phòng ban',
    groupId: 'hr',
    description: 'Phòng ban nào đi làm đều, ai hay đi muộn hay vắng, và kết quả KPI công việc ra sao.',
    permissionKey: 'hrm.reports.view',
    isNew: true,
    component: lazy(() => import('./HrAttendanceKpiReport')),
  },
  {
    // TCR-2537 — trang tra cứu định nghĩa chỉ số dùng chung (đọc GET /report-center/metrics).
    id: 'metric-definitions',
    name: 'Định nghĩa chỉ số',
    groupId: 'operations',
    description: 'Mỗi chỉ số báo cáo được tính thế nào: công thức, nguồn dữ liệu và lưu ý — tìm theo tên.',
    permissionKey: 'reports.sales.overview.view',
    component: lazy(() => import('./MetricDefinitionsPage')),
  },
  {
    id: 'sales-overview-demo',
    name: 'Bán hàng theo ngày (báo cáo mẫu)',
    groupId: 'sales',
    description: 'Báo cáo mẫu dựng bằng dữ liệu giả để minh hoạ khung — ticket báo cáo sẽ thay bằng API thật.',
    permissionKey: 'reports.sales.overview.view',
    component: lazy(() => import('./ExampleReport')),
  },
];

/**
 * Hợp OR mọi permission key của registry — gate MỞ NHÓM menu và mở route
 * /report-center (người thấy ít nhất 1 báo cáo thì nên thấy trung tâm).
 */
export const REPORT_CENTER_ANY_PERMISSIONS: readonly string[] = [
  ...new Set(REPORT_REGISTRY.map((entry) => entry.permissionKey)),
];

export function getReportEntry(id: string | undefined): ReportRegistryEntry | undefined {
  if (!id) return undefined;
  return REPORT_REGISTRY.find((entry) => entry.id === id);
}

/** Lọc báo cáo theo hàm quyền (OR key) — dùng chung cho trang danh mục và outlet. */
export function getVisibleReports(
  hasPermission: (key: string) => boolean,
): ReportRegistryEntry[] {
  return REPORT_REGISTRY.filter((entry) => hasPermission(entry.permissionKey));
}
